package dev.lingvo.shared

import kotlinx.serialization.Serializable

/** Someone with access to a workspace, as shown on the project's Members tab. */
@Serializable
data class MemberDto(
    val userId: String,
    val email: String,
    val name: String,
    val avatarColor: String,
    val role: Role,
    val joinedAt: String,
    /** True for the caller's own membership, so the UI can stop them removing themselves by accident. */
    val isSelf: Boolean = false,
    /**
     * Whether this member may approve translations. A TRANSLATOR with this set is a **Reviewer**,
     * and costs no billable seat — see [canApprove].
     */
    val canApprove: Boolean = false,
    /**
     * What the caller may do to this member, decided by [capabilitiesFor] on the server.
     *
     * Per row rather than per page: whether OWNER may be granted depends on who is asking, whether
     * the row may be demoted depends on whether it is the last owner, and whether a promotion is
     * affordable depends on the plan. One page-level flag cannot express any of that.
     */
    val capabilities: MemberCapabilities = MemberCapabilities(),
)

/**
 * A pending or completed invitation.
 *
 * Never carries the token: it exists in plaintext only in the response that created it, and only a
 * hash is stored.
 */
@Serializable
data class InvitationDto(
    val id: String,
    val email: String,
    val role: Role,
    val createdAt: String,
    val expiresAt: String,
    val acceptedAt: String? = null,
    val revokedAt: String? = null,
) {
    val state: InvitationState
        get() = when {
            revokedAt != null -> InvitationState.REVOKED
            acceptedAt != null -> InvitationState.ACCEPTED
            else -> InvitationState.PENDING
        }
}

@Serializable
enum class InvitationState { PENDING, ACCEPTED, REVOKED }

@Serializable
data class CreateInvitationRequest(
    val email: String,
    val role: Role,
)

/**
 * The one and only time the invitation link is available.
 *
 * There is no email delivery yet, so the inviter copies [token] and sends it themselves — the same
 * shown-once contract as an API token.
 */
@Serializable
data class CreatedInvitationDto(
    val invitation: InvitationDto,
    val token: String,
)

/**
 * A change to one membership. Every field is optional and null means "leave this alone", so the
 * reviewer toggle and the role select can each send only what they changed.
 */
@Serializable
data class UpdateMemberRequest(
    val role: Role? = null,
    /**
     * Sets the reviewer flag. Only meaningful on a TRANSLATOR, which is the only role the toggle is
     * offered on — for OWNER and ADMIN "follow the role" already grants approval.
     */
    val canApprove: Boolean? = null,
    /**
     * The billable-seat count the confirmation modal was showing when the customer agreed to it.
     *
     * Sent back so the server can refuse a change whose price has moved since. Two admins acting at
     * once would otherwise make the modal promise "$19 more" while the invoice says $38 — a false
     * statement about money, on the screen where the customer decides whether to trust us.
     */
    val expectedBillableSeats: Int? = null,
)

@Serializable
data class AcceptInvitationRequest(
    val token: String,
)

@Serializable
data class AcceptedInvitationDto(
    val organization: OrgDto,
    val role: Role,
)

/**
 * The members page, version 2.
 *
 * The page-level `canManage` boolean it replaced was too coarse to be correct: it said "you may
 * manage members" and the interface then offered every role to everyone who had it, including
 * OWNER to an admin the server would refuse. What may be done is a property of each row — see
 * [MemberDto.capabilities] — and this envelope carries only what the header and the price
 * confirmation need.
 */
@Serializable
data class MembersResponse(
    val members: List<MemberDto> = emptyList(),
    val invitations: List<InvitationDto> = emptyList(),
    /** Whether the invite control is shown at all. Per-row rules still govern each existing member. */
    val canInvite: Boolean = false,
    /** Read by the header and by the seat-price confirmation, so neither has to type a number. */
    val usage: UsageDto? = null,
)

/**
 * Handing a workspace over.
 *
 * The password is required by the route, not by politeness: a session left open on an unlocked
 * laptop is the threat, and this is the one action in a workspace that removes the ability to undo
 * anything else.
 */
@Serializable
data class TransferOwnershipRequest(val userId: String, val password: String)

/**
 * Deleting a workspace.
 *
 * [confirmSlug] must match exactly. A slug is the one string somebody cannot produce by reflex,
 * which is the whole point of asking for it rather than showing a second "are you sure".
 */
@Serializable
data class DeleteWorkspaceRequest(val confirmSlug: String, val password: String)

/** What a deletion would destroy, counted rather than estimated. */
@Serializable
data class WorkspaceDeletionScopeDto(
    val slug: String,
    val projects: Int = 0,
    val keys: Int = 0,
    val translations: Int = 0,
    val glossaryTerms: Int = 0,
    /**
     * A live subscription blocks the delete, server-side.
     *
     * Surfaced so the page can say why and link to cancelling, rather than presenting a button that
     * fails — but the refusal itself is in the handler, because a disabled button is not a guard.
     */
    val blockedBySubscription: Boolean = false,
)
